import type { SystemHealthStats } from '../../../types/systemHealth'

interface DailyStatsSectionProps {
  stats: SystemHealthStats | undefined
  isLoading: boolean
}

export function DailyStatsSection({ stats, isLoading }: DailyStatsSectionProps) {
  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Message statistics</h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !stats ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <Stat
              label="Total since deployment"
              value={stats.totalSinceDeployment.toLocaleString()}
              sub={stats.deploymentTimestamp}
            />
            <Stat label="Today" value={stats.today.toLocaleString()} />
            <Stat label="Unique devices" value={stats.uniqueDevices.toLocaleString()} />
          </div>

          <h3 className="text-sm font-medium text-gray-700 mb-2">Daily breakdown</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
              <tr>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4 text-right">Messages</th>
              </tr>
            </thead>
            <tbody>
              {stats.daily.map((d) => (
                <tr key={d.date} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 font-mono text-gray-700">{d.date}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{d.messages.toLocaleString()}</td>
                </tr>
              ))}
              {stats.daily.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-3 text-center text-gray-500">
                    No data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}
